/**
 * Visx Geo (bubble map) implementation.
 *
 * World map rendered via @visx/geo (which is a thin React wrapper over d3-geo)
 * + the world-atlas TopoJSON. Country shapes drawn as paths; bubbles placed at
 * each datum's lat/lon, sized by `value` (sessions), color-scaled by `count`
 * (active users) when available.
 *
 * Used by:
 *   - Active Users 2.13 "Sessions by Country" (primary)
 *   - Any future geo widget on Active Endpoints / Overview
 *
 * Recharts has no native geo support — Chart.tsx routes `type='geo'` from the
 * Recharts adapter to this Visx implementation (Option B fallback).
 */

import { useMemo, useState, useEffect } from 'react';
import { ParentSize } from '@visx/responsive';
import { Mercator } from '@visx/geo';
import { scaleLinear, scaleSqrt } from '@visx/scale';
import { useTooltip, TooltipWithBounds, defaultStyles } from '@visx/tooltip';
import * as topojson from 'topojson-client';
import type { FeatureCollection, Geometry } from 'geojson';
import type { GeoChartProps, CommonChartProps, GeoDatum } from '../types';
import { chartTheme } from '../theme';
import { DrillAwareTooltip } from '@/components/analytics/DrillAwareTooltip';

type Props = GeoChartProps & CommonChartProps;

// world-atlas countries-110m is ~100KB unminified — small enough to ship inline-ish.
// We dynamic-import it so the geo chart's atlas isn't loaded on dashboards that
// don't render a geo widget.
let cachedAtlas: FeatureCollection<Geometry> | null = null;
async function loadWorldAtlas(): Promise<FeatureCollection<Geometry>> {
  if (cachedAtlas) return cachedAtlas;
  const mod = (await import('world-atlas/countries-110m.json')) as unknown as {
    default?: unknown;
  };
  const raw = (mod.default ?? mod) as Parameters<typeof topojson.feature>[0];
  const objects = (raw as { objects: Record<string, unknown> }).objects;
  const fc = topojson.feature(
    raw,
    objects.countries as Parameters<typeof topojson.feature>[1]
  ) as FeatureCollection<Geometry>;
  cachedAtlas = fc;
  return fc;
}

export function VisxGeo(props: Props): JSX.Element {
  return (
    <ParentSize>
      {({ width, height }) =>
        width > 0 && height > 0 ? <VisxGeoInner {...props} width={width} height={height} /> : null
      }
    </ParentSize>
  );
}

function VisxGeoInner({
  data,
  width,
  height,
}: Props & { width: number; height: number }): JSX.Element {
  const theme = chartTheme('visx').theme;
  const palette = theme.categoricalPalette;
  const [atlas, setAtlas] = useState<FeatureCollection<Geometry> | null>(cachedAtlas);
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, showTooltip, hideTooltip } =
    useTooltip<GeoDatum>();

  useEffect(() => {
    if (atlas) return;
    let cancelled = false;
    loadWorldAtlas().then((fc) => {
      if (!cancelled) setAtlas(fc);
    });
    return () => {
      cancelled = true;
    };
  }, [atlas]);

  // Scales for bubble size + color.
  const { sizeScale, colorScale } = useMemo(() => {
    const sessions = data.map((d) => d.value ?? 0);
    const maxSessions = Math.max(1, ...sessions);
    const counts = data.map((d) => Number(d.activeUsers ?? 0));
    const maxCount = Math.max(1, ...counts);
    return {
      sizeScale: scaleSqrt({ domain: [0, maxSessions], range: [3, Math.min(48, width * 0.04)] }),
      colorScale: scaleLinear<string>({
        domain: [0, maxCount],
        range: [palette[3], palette[0]], // teal-light → teal-dark
      }),
    };
  }, [data, width, palette]);

  // Projection: Mercator scaled to fit a centered world view in the container.
  const projectionScale = (width / 640) * 100; // 640 = native Mercator design width

  return (
    <div style={{ position: 'relative', width, height }}>
      {!atlas ? (
        <div className="dls-flex dls-items-center dls-justify-center dls-h-full dls-text-sm dls-text-muted-foreground">
          Loading world atlas…
        </div>
      ) : (
        <svg width={width} height={height} aria-label="Sessions by country">
          <Mercator
            data={atlas.features}
            scale={projectionScale}
            translate={[width / 2, height / 2 + height * 0.15]}
          >
            {({ features, projection }) => (
              <g>
                {features.map(({ path }: { path: string | null }, i: number) => (
                  <path
                    key={`country-${i}`}
                    d={path ?? ''}
                    fill="rgb(244, 246, 245)"
                    stroke="rgb(220, 224, 222)"
                    strokeWidth={0.5}
                  />
                ))}
                {data.map((d, i) => {
                  const projected = projection([d.longitude, d.latitude]);
                  if (!projected) return null;
                  const [cx, cy] = projected;
                  const r = sizeScale(d.value ?? 0);
                  const fill = colorScale(Number(d.activeUsers ?? 0));
                  return (
                    <circle
                      key={`bubble-${d.id ?? i}`}
                      cx={cx}
                      cy={cy}
                      r={r}
                      fill={fill}
                      fillOpacity={0.7}
                      stroke="#fff"
                      strokeWidth={1.5}
                      style={{ cursor: 'pointer' }}
                      onMouseMove={(e) => {
                        const rect = (e.currentTarget.ownerSVGElement as SVGSVGElement | null)?.getBoundingClientRect();
                        showTooltip({
                          tooltipLeft: e.clientX - (rect?.left ?? 0),
                          tooltipTop: e.clientY - (rect?.top ?? 0),
                          tooltipData: d,
                        });
                      }}
                      onMouseLeave={hideTooltip}
                    />
                  );
                })}
              </g>
            )}
          </Mercator>
        </svg>
      )}
      {tooltipOpen && tooltipData && (
        <TooltipWithBounds
          top={tooltipTop}
          left={tooltipLeft}
          style={{ ...defaultStyles, background: 'transparent', padding: 0, boxShadow: 'none' }}
        >
          <DrillAwareTooltip
            header={tooltipData.label}
            rows={[
              { color: palette[0], label: 'Sessions', value: tooltipData.value ?? 0 },
              {
                color: palette[3],
                label: 'Active Users',
                value: Number(tooltipData.activeUsers ?? 0),
              },
            ]}
          />
        </TooltipWithBounds>
      )}
    </div>
  );
}
